import { LegacyReportSummaryItem } from "../api/LegacyReportsApi";
import { useState, useEffect } from "react";
import LegacyPostView from "./posts/LegacyPostView";
import FilterCheckbox from "./common/FilterCheckbox";
import CollapsibleSection from "./common/CollapsibleSection";
import { formatBehaviorDisplay } from "./common/util";
import LegacyArticleView from "./articles/LegacyArticleView";

export interface FilterState {
  showPosts: boolean;
  showArticles: boolean;
  showAdverseBehaviors: boolean;
  adverseBehaviors: {
    CANNABIS: boolean;
    CRIMINAL: boolean;
    HARASSMENT: boolean;
    INTOLERANCE: boolean;
    OTHER_DRUGS: boolean;
    SEX: boolean;
    THREAT: boolean;
    VIOLENCE: boolean;
  };
  showPositiveBehaviors: boolean;
  positiveBehaviors: {
    COMMUNITY: boolean;
  };
  showKeywords: boolean;
  keywords: Map<string, boolean>;
  showRelevantOnlyArticles: boolean;
  postTypes: {
    ORIGINAL: boolean;
    SHARED: boolean;
    LIKED: boolean;
  };
}

function ContentFilters({
  metrics,
  filters,
  setFilters,
}: {
  metrics: LegacyReportSummaryItem["webPersonMetrics"];
  filters: FilterState;
  setFilters: (
    filters: FilterState | ((prev: FilterState) => FilterState)
  ) => void;
}) {
  const togglePostType = (type: keyof FilterState["postTypes"]) => {
    setFilters((prev) => ({
      ...prev,
      postTypes: {
        ...prev.postTypes,
        [type]: !prev.postTypes[type],
      },
    }));
  };

  const toggleBehavior = (behavior: keyof FilterState["adverseBehaviors"]) => {
    setFilters((prev) => {
      const newAdverseBehaviors = {
        ...prev.adverseBehaviors,
        [behavior]: !prev.adverseBehaviors[behavior],
      };
      const anyBehaviorEnabled = Object.values(newAdverseBehaviors).some(
        (value) => value
      );
      return {
        ...prev,
        showAdverseBehaviors: anyBehaviorEnabled,
        adverseBehaviors: newAdverseBehaviors,
      };
    });
  };

  const toggleKeyword = (keyword: string) => {
    setFilters((prev) => ({
      ...prev,
      keywords: new Map(prev.keywords).set(
        keyword,
        !prev.keywords.get(keyword)
      ),
    }));
  };

  const toggleShowAdverseBehaviors = () => {
    setFilters((prev) => {
      const newShowAdverseBehaviors = !prev.showAdverseBehaviors;
      const newAdverseBehaviors = Object.entries(prev.adverseBehaviors).map(
        ([behavior, value]) => {
          return [behavior, newShowAdverseBehaviors];
        }
      );
      const newFilters = {
        ...prev,
        showAdverseBehaviors: newShowAdverseBehaviors,
        adverseBehaviors: Object.fromEntries(newAdverseBehaviors),
      };
      return newFilters;
    });
  };

  const toggleShowKeywords = () => {
    setFilters((prev) => {
      const newShowKeywords = !prev.showKeywords;
      const newKeywords = new Map(
        Array.from(prev.keywords.entries()).map(([keyword]) => [
          keyword,
          newShowKeywords,
        ])
      );
      return {
        ...prev,
        showKeywords: newShowKeywords,
        keywords: newKeywords,
      };
    });
  };

  return (
    <div className="h-[98%] overflow-y-auto px-4 pt-5">
      <div className="space-y-4">
        <div className="flex flex-col space-y-2">
          <FilterCheckbox
            label="Posts"
            count={metrics.totalPostFlagCount}
            checked={filters.showPosts}
            onChange={() =>
              setFilters((prev) => ({ ...prev, showPosts: !prev.showPosts }))
            }
            className={`${
              filters.showPosts ? "text-blue-500" : "text-gray-500"
            }`}
          />
          <FilterCheckbox
            label="News"
            count={metrics.totalNewsFlagCount}
            checked={filters.showArticles}
            onChange={() =>
              setFilters((prev) => ({
                ...prev,
                showArticles: !prev.showArticles,
              }))
            }
            className={`${
              filters.showArticles ? "text-blue-500" : "text-gray-500"
            }`}
          />
        </div>

        <hr className="border-t-2 border-gray-300" />

        <CollapsibleSection title="Behaviors">
          <FilterCheckbox
            label="Adverse Content"
            count={metrics.badNewsFlagCount + metrics.badPostFlagCount}
            checked={filters.showAdverseBehaviors}
            onChange={toggleShowAdverseBehaviors}
            className={`${
              filters.showAdverseBehaviors ? "text-blue-500" : "text-gray-500"
            }`}
          />
          <div className="ml-2 space-y-2">
            {Object.entries(filters.adverseBehaviors).map(
              ([behavior, checked]) => {
                const totalCount =
                  metrics.postFlagCountsByType[behavior] +
                  metrics.newsFlagCountsByType[behavior];
                return totalCount > 0 ? (
                  <FilterCheckbox
                    key={behavior}
                    label={formatBehaviorDisplay(behavior)}
                    count={
                      metrics.postFlagCountsByType[behavior] +
                      metrics.newsFlagCountsByType[behavior]
                    }
                    checked={checked}
                    onChange={() =>
                      toggleBehavior(
                        behavior as keyof FilterState["adverseBehaviors"]
                      )
                    }
                    className={`${checked ? "text-blue-500" : "text-gray-500"}`}
                  />
                ) : null;
              }
            )}
          </div>
          <FilterCheckbox
            label="Community"
            count={
              metrics.postFlagCountsByType["COMMUNITY"] +
              metrics.newsFlagCountsByType["COMMUNITY"]
            }
            checked={filters.positiveBehaviors.COMMUNITY}
            onChange={() =>
              setFilters((prev) => ({
                ...prev,
                positiveBehaviors: {
                  COMMUNITY: !prev.positiveBehaviors.COMMUNITY,
                },
              }))
            }
            className={`${
              filters.positiveBehaviors.COMMUNITY
                ? "text-blue-500"
                : "text-gray-500"
            }`}
          />
        </CollapsibleSection>

        {filters.showArticles && (
          <CollapsibleSection title="Relevant News">
            <FilterCheckbox
              label="Relevant"
              count={metrics.relevantNewsFlagCount}
              checked={filters.showRelevantOnlyArticles}
              onChange={() =>
                setFilters((prev) => ({
                  ...prev,
                  showRelevantOnlyArticles: !prev.showRelevantOnlyArticles,
                }))
              }
              className={`${
                filters.showRelevantOnlyArticles
                  ? "text-blue-500"
                  : "text-gray-500"
              }`}
            />
          </CollapsibleSection>
        )}

        <CollapsibleSection title="Keywords">
          <FilterCheckbox
            label="Keywords"
            count={metrics.keywordNewsFlagCount + metrics.keywordPostFlagCount}
            checked={filters.showKeywords}
            onChange={toggleShowKeywords}
            className={`${
              filters.showKeywords ? "text-blue-500" : "text-gray-500"
            }`}
          />
          <div className="ml-2 space-y-2">
            {Array.from(filters.keywords.entries()).map(
              ([keyword, checked]) => {
                const totalKeywordCount =
                  metrics.postFlagCountsByKeyword[keyword] +
                  metrics.newsFlagCountsByKeyword[keyword];
                return totalKeywordCount > 0 ? (
                  <FilterCheckbox
                    key={keyword}
                    label={keyword}
                    count={totalKeywordCount}
                    checked={checked}
                    onChange={() => toggleKeyword(keyword)}
                    className={`${checked ? "text-blue-500" : "text-gray-500"}`}
                  />
                ) : null;
              }
            )}
          </div>
        </CollapsibleSection>

        {filters.showPosts && (
          <CollapsibleSection title="Post Types">
            <div className="ml-2 space-y-2">
              {Object.entries(filters.postTypes).map(([type, checked]) => (
                <FilterCheckbox
                  key={type}
                  label={type.charAt(0) + type.slice(1).toLowerCase()}
                  count={metrics.postFlagTypeCountsByPostType[type] || 0}
                  checked={checked}
                  onChange={() =>
                    togglePostType(type as keyof FilterState["postTypes"])
                  }
                  className={`${checked ? "text-blue-500" : "text-gray-500"}`}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
}

function LegacyReportSummaryContent({
  report,
}: {
  report: LegacyReportSummaryItem;
}) {
  const [filters, setFilters] = useState<FilterState>({
    showPosts: true,
    showArticles: true,
    showAdverseBehaviors: true,
    adverseBehaviors: {
      CANNABIS: true,
      CRIMINAL: true,
      HARASSMENT: true,
      INTOLERANCE: true,
      OTHER_DRUGS: true,
      SEX: true,
      THREAT: true,
      VIOLENCE: true,
    },
    showPositiveBehaviors: true,
    positiveBehaviors: {
      COMMUNITY: true,
    },
    showKeywords: true,
    keywords: new Map(),
    showRelevantOnlyArticles: true,
    postTypes: {
      ORIGINAL: true,
      SHARED: true,
      LIKED: true,
    },
  });

  const [enabledBehaviors, setEnabledBehaviors] = useState<Array<string>>([]);
  const [enabledKeywords, setEnabledKeywords] = useState<Array<string>>([]);
  const [enabledPostTypes, setEnabledPostTypes] = useState<Array<string>>([]);
  const [showRelevantOnlyArticles, setShowRelevantOnlyArticles] =
    useState<boolean>(false);

  // Combine keywords across posts and news, excluding those with a total count of zero
  useEffect(() => {
    const combinedKeywords = new Map<string, boolean>();
    Object.entries(report.webPersonMetrics.postFlagCountsByKeyword).forEach(
      ([keyword, count]) => {
        const totalKeywordCount =
          count +
          (report.webPersonMetrics.newsFlagCountsByKeyword[keyword] || 0);
        if (totalKeywordCount > 0) {
          combinedKeywords.set(keyword, true);
        }
      }
    );
    Object.entries(report.webPersonMetrics.newsFlagCountsByKeyword).forEach(
      ([keyword, count]) => {
        const totalKeywordCount =
          count +
          (report.webPersonMetrics.postFlagCountsByKeyword[keyword] || 0);
        if (totalKeywordCount > 0) {
          combinedKeywords.set(keyword, true);
        }
      }
    );
    setFilters((prev) => ({
      ...prev,
      keywords: combinedKeywords,
    }));
  }, [
    report.webPersonMetrics.postFlagCountsByKeyword,
    report.webPersonMetrics.newsFlagCountsByKeyword,
    setFilters,
  ]);

  // Calculate enabled behaviors list from filter state
  useEffect(() => {
    const enabledAdverseBehaviors = Object.entries(filters.adverseBehaviors)
      .filter(([_, isEnabled]) => filters.showAdverseBehaviors && isEnabled)
      .map(([flag]) => flag);
    const enabledPositiveBehaviors = Object.entries(filters.positiveBehaviors)
      .filter(([_, isEnabled]) => filters.showPositiveBehaviors && isEnabled)
      .map(([flag]) => flag);
    setEnabledBehaviors([
      ...enabledAdverseBehaviors,
      ...enabledPositiveBehaviors,
    ]);
  }, [
    filters.adverseBehaviors,
    filters.showAdverseBehaviors,
    filters.positiveBehaviors,
    filters.showPositiveBehaviors,
    setEnabledBehaviors,
  ]);

  // Calculate enabled keywords list from filter state
  useEffect(() => {
    const enabledKeywords = Array.from(filters.keywords.entries())
      .filter(([_, isEnabled]) => filters.showKeywords && isEnabled)
      .map(([keyword]) => keyword);
    setEnabledKeywords(enabledKeywords);
  }, [filters.keywords, filters.showKeywords, setEnabledKeywords]);

  // Calculate enabled post types list from filter state
  useEffect(() => {
    const enabledPostTypes = Object.entries(filters.postTypes)
      .filter(([_, isEnabled]) => isEnabled)
      .map(([type]) => type);
    setEnabledPostTypes(enabledPostTypes);
  }, [filters.postTypes, setEnabledPostTypes]);

  // Set hideRelevantNews boolean from filter state
  useEffect(() => {
    setShowRelevantOnlyArticles(filters.showRelevantOnlyArticles);
  }, [filters.showRelevantOnlyArticles, setShowRelevantOnlyArticles]);

  return (
    <div className="w-full bg-[#E0E8E8]">
      <div className="flex">
        <div className="w-1/4">
          <ContentFilters
            metrics={report.webPersonMetrics}
            filters={filters}
            setFilters={setFilters}
          />
        </div>
        <div className="w-3/4">
          {filters.showPosts && (
            <LegacyPostView
              report={report}
              enabledPostTypes={enabledPostTypes}
              enabledBehaviors={enabledBehaviors}
              enabledKeywords={enabledKeywords}
            />
          )}
          {filters.showArticles && (
            <LegacyArticleView
              report={report}
              showRelevantOnlyArticles={showRelevantOnlyArticles}
              enabledBehaviors={enabledBehaviors}
              enabledKeywords={enabledKeywords}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default LegacyReportSummaryContent;
